/*
 * Proprietary and Confidential
 *
 * Copyright (c) [2018] -  [] Marcelo H. Krebber - Munich, London 2018
 * All Rights Reserved.
 *
 * Dissemination or reproduction of this file [locations.js] or parts within
 * via any medium is strictly forbidden unless prior written permission is obtained
 * from <marcelo.krebber@gmail.com>
 *
 * Last modified: 20.11.19, 16:27
 */

import {generateAvatar} from "../components/billboard-cover/generate-avatar";
import {YOUTUBE_REGEX} from "./index";
import {getLocaleFromLocalStorage} from "./local-storage";

export const environment = {
    /* server locations */
    ROOT_STATIC_URL: window._env_.REACT_APP_ROOT_STATIC_URL,
    ROOT_SERVER_URL: window._env_.REACT_APP_ROOT_SERVER_URL,
    ROOT_CLIENT_URL: window._env_.REACT_APP_ROOT_CLIENT_URL,
    ROOT_STOMP_SERVER: window._env_.REACT_APP_STOMP_SERVER,
    ROOT_USER_URL: window._env_.REACT_APP_SERVER_SECURE_URL,
    ROOT_PUBLIC_URL: window._env_.REACT_APP_SERVER_PUBLIC_URL,
    IMAGE_SERVER_PUBLIC_URL: window._env_.REACT_APP_IMAGE_SERVER_URL,
    DEFAULT_PUBLIC_USER: window._env_.REACT_APP_PUBLIC_USER,
    DEFAULT_COPY_FILE: window._env_.REACT_APP_DEFAULT_COPY_FILE,
    DEFAULT_THEME: window._env_.REACT_APP_DEFAULT_THEME,
    DEFAULT_LOCALE: window._env_.REACT_APP_DEFAULT_LOCALE,

    /* default reserved page names */
    PRIVACY_POLICY_PAGE: window._env_.REACT_APP_PRIVACY_POLICY_PAGE,
    CONTACT_PAGE: window._env_.REACT_APP_CONTACT_PAGE,
    TERMS_OF_USE_PAGE: window._env_.REACT_APP_TERMS_OF_USE_PAGE,
    NOT_FOUND_PAGE: window._env_.REACT_APP_NOT_FOUND_PAGE,
    IMPRINT_PAGE: window._env_.REACT_APP_IMPRINT_PAGE,
};

export const PUBLIC_URL = '%PUBLIC_URL%';

export const getPublicUser = () => environment.DEFAULT_PUBLIC_USER;

export function getLocale() {
    const locale = getLocaleFromLocalStorage();
    return locale !== null ? locale : environment.DEFAULT_LOCALE;
}

export const getLanguageScriptName = (locale) => `language_${locale}.js`;
export const getCopyScriptName = (locale) => `copy_${locale}.js`;

export const getStompServerUrl = () => environment.ROOT_STOMP_SERVER;

export const getPrivacyPolicyPageUrl = () => environment.PRIVACY_POLICY_PAGE;

export const getTermsOfUsePageUrl = () => environment.TERMS_OF_USE_PAGE;

export const getDefaultTheme = () => environment.DEFAULT_THEME;

export const getImprintPageUrl = () => environment.IMPRINT_PAGE;

// @Deprecated - use image server instead
export const getStaticImageUrl = imagename => {
    console.log('Deprecated call', imagename);
    if (imagename && imagename.indexOf(PUBLIC_URL) !== -1) {
        return `${process.env.PUBLIC_URL}/static` + imagename.slice(PUBLIC_URL.length);
    }
    return `${environment.ROOT_STATIC_URL}/${imagename}`
};
// achtung! resources are currently not secured
export const getStaticVideoUrl = (videoPath) => `${environment.ROOT_STATIC_URL}/${videoPath}`;

// achtung! resources are currently not secured / path for videos and pics
export const getLandingPageMediaUrl = (videoPath) => `${environment.ROOT_STATIC_URL}/application/landingpage/${videoPath}`;

// served by image server
// changed to no resizing since at upload time large images are compressed anyway
export const getImageServerFullUrl = (imagename, isMobile=false) => {
    return isMobile
        ? `${environment.IMAGE_SERVER_PUBLIC_URL}/rs:fit:2000:1400:no:0/plain/local:///${imagename}@jpg`
        : `${environment.IMAGE_SERVER_PUBLIC_URL}/plain/local:///${imagename}@jpg`
}

// served by image server
export const getImageServerMediumUrl = imagename => {
    return `${environment.IMAGE_SERVER_PUBLIC_URL}/rs:fit:1400:900:no:0/plain/local:///${imagename}@jpg`
}

// served by image server
export const getImageServerSmallUrl = imagename => {
    return `${environment.IMAGE_SERVER_PUBLIC_URL}/rs:fit:600:400:no:0/plain/local:///${imagename}@jpg`
}

// served by image server
export const getImageServerThumbsUrl = imagename => {
    return `${environment.IMAGE_SERVER_PUBLIC_URL}/rs:fill:400:400:no:0/plain/local:///${imagename}@jpg`
};

export const getYoutubeThumbnail = url => {
    YOUTUBE_REGEX.lastIndex = 0;
    const videoId = YOUTUBE_REGEX.exec(url);
    return videoId ? `https://img.youtube.com/vi/${videoId[1]}/hqdefault.jpg` : '';
}

export const getUserAvatar = user => {
    return user.avatar ? getImageServerThumbsUrl(user.avatar) : generateAvatar(user.firstname, user.lastname);
}

export const getUserAvatarByName = (url, firstname, lastname) => {
    return url ? getImageServerThumbsUrl(url) : generateAvatar(firstname, lastname);
}

export const getPeekParentSpacesUrl = (username, type) => `${environment.ROOT_USER_URL}/${username}/spaces/peek/${type}`;
export const getPeekEventsFromDateUrl = (username, isodate) => `${environment.ROOT_USER_URL}/${username}/spaces/peek/fromdate/${isodate}`;
export const getPeekPublicEventsFromDateUrl = (username, isodate) => `${environment.ROOT_PUBLIC_URL}/${username}/spaces/peek/fromdate/${isodate}`;
export const getPeekPublicSpacesUrl = (username, type) => `${environment.ROOT_PUBLIC_URL}/${username}/spaces/peek/${type}`;
export const getPeekMemberOfSpacesUrl = (username, type) => `${environment.ROOT_USER_URL}/${username}/spaces/peek/${type}/member`;
export const getPeekFeaturedSpacesUrl = (username, type)  => `${environment.ROOT_USER_URL}/${username}/spaces/peek/${type}/preview`;
export const getPeekPublicFeaturedSpacesUrl = (username, type) => `${environment.ROOT_PUBLIC_URL}/${username}/spaces/peek/${type}/preview`;

// @Deprecated
export const getParentSpacesUrl = (username, type) => `${environment.ROOT_USER_URL}/${username}/spaces/${type}`;

export const getCurrentDateSpacesUrl = (username, type) => `${environment.ROOT_USER_URL}/${username}/spaces/${type}/current`;

export const getMemberOfSpacesUrl = (username, type) => `${environment.ROOT_USER_URL}/${username}/spaces/${type}/member`;
export const getShareablePeekSpacesUrl = (username, type) => `${environment.ROOT_USER_URL}/${username}/spaces/peek/${type}/share`;

export const getPublicParentSpacesUrl = (username, type) => `${environment.ROOT_PUBLIC_URL}/${username}/spaces/${type}`;

export const getPublicCurrentDateSpacesUrl = (username, type) => `${environment.ROOT_PUBLIC_URL}/${username}/spaces/${type}/current`;

// see asyncFetchParentSpaces for usage
export const getFetchSpacesEventType = (type, context) => {
    const contextType = context ? `_${context}` : '';
    return `FETCH_${type.toUpperCase()}${contextType}`;
}

export const getGenericSpaceUrl = (username, spaceId) => `${environment.ROOT_USER_URL}/${username}/space/generic/${spaceId}`;

export const getGenericSpaceByIdUrl = (username, spaceId) => `${environment.ROOT_USER_URL}/${username}/space/generic/id/${spaceId}`;
export const getPeekSpaceByIdUrl = (username, spaceId) => `${environment.ROOT_USER_URL}/${username}/space/peek/id/${spaceId}`;

export const getGenericSpaceDataUrl = (username, space) => `${environment.ROOT_USER_URL}/${username}/space/generic/spacedata/${space}`;

export const getRootServerUrl = path => `${environment.ROOT_SERVER_URL}/${path}`;

export const getPostsUploadUrl = username => `${environment.ROOT_SERVER_URL}/user/${username}/posts/upload`;

export const getAvatarUploadUrl = username => `${environment.ROOT_SERVER_URL}/user/${username}/avatar/upload`;

export const getLoginUrl = () => `${environment.ROOT_SERVER_URL}/public/login`;

export const getRequestPasswordUrl = (username) => `${environment.ROOT_SERVER_URL}/public/password/${username}/request/reset`;

export const getResetPasswordUrl = (username) => `${environment.ROOT_SERVER_URL}/public/password/${username}/reset`;

export const getConfirmEmailUrl = () => `${environment.ROOT_SERVER_URL}/public/email/confirm`;

export const getUserChatEntries = (username, chatId) => `${environment.ROOT_USER_URL}/${username}/chat/${chatId}/entries`;

// create user in public mode
export const getCreateUserUrl = (username) => `${environment.ROOT_PUBLIC_URL}/user/create/${username}`;

export const getCreatePageUrl = (username, name) => `${environment.ROOT_USER_URL}/${username}/page/${name}/create`;

export const getFetchPageUrl = (username, pageId) => `${environment.ROOT_USER_URL}/${username}/page/${pageId}/id`;

export const getFetchPageByNameUrl = (username, pagename) => `${environment.ROOT_USER_URL}/${username}/page/${pagename}`;

export const getFetchPublicPageByNameUrl = (username, pagename) => `${environment.ROOT_PUBLIC_URL}/${username}/page/${pagename}`;

export const getDeletePageUrl = (username, pageId) => `${environment.ROOT_USER_URL}/${username}/page/${pageId}/id`;

// actually same as delete space since a gallery is just that - a space with lots of images
export const getDeleteGalleryUrl = (username, galleryId) => `${environment.ROOT_USER_URL}/${username}/space/${galleryId}/delete`;

export const getFetchPageListUrl = (username) => `${environment.ROOT_USER_URL}/${username}/pages/list`;

// state one of 'active' or 'archived'
// authority for superadmin or admin users
export const getPeekPagesByStateUrl = (username, state) => `${environment.ROOT_USER_URL}/${username}/pages/list/${state}`;

// same as getPeekPagesByStateUrl but with authority for all logged in users
export const getPeekMenuByStateUrl = (username, state) => `${environment.ROOT_USER_URL}/${username}/pages/menu/${state}`;

// same as getPeekPagesByStateUrl but with authority for all logged in users
export const getPeekPublicMenuByStateUrl = (username, state) => `${environment.ROOT_PUBLIC_URL}/${username}/pages/menu/${state}`;

// state one of 'active' or 'archived'
// authority for superadmin or admin users
export const getPeekSpacesUrl = (username, state) => `${environment.ROOT_USER_URL}/${username}/spaces/peek/${state}`;
export const getPageablePeekSpacesUrl = (username, state, page, size) => `${environment.ROOT_USER_URL}/${username}/spaces/peek/${state}/${page}/${size}`;

// authority is USER as in username, state is currently 'active' only
// fetches galleries specific to a user
export const getPeekGallerySpacesUrl = (username, state) => `${environment.ROOT_USER_URL}/${username}/galleries/peek/${state}`;

// fetch galleries specific to a group space and not to a user
export const getPeekGallerySpacesBySpaceUrl = (username, spaceId) => `${environment.ROOT_USER_URL}/${username}/galleries/peek/group/${spaceId}`;

// fetches public galleries specific to a user ie public superuser
export const getPeekPublicGallerySpacesUrl = (username, state) => `${environment.ROOT_PUBLIC_URL}/${username}/galleries/peek/${state}`;

// watch spaceMedia is not the same as post media ie simply media
export const getSpaceMediaUrl = (username, spaceId) => `${environment.ROOT_USER_URL}/${username}/galleries/media/${spaceId}`;
// same as above but with paging support
export const getSpaceMediaPagedUrl = (username, spaceId, mediatype, page, size) => `${environment.ROOT_USER_URL}/${username}/galleries/media/${spaceId}/${mediatype}/${page}/${size}`;
// return media as list
export const getHomeSpaceMediaUrl = (username) => `${environment.ROOT_USER_URL}/${username}/posts/spacemedia/home`;
// same as spacemedia above but returns a page object which in turn include media list and page information
export const getHomeSpaceMediaPagedUrl = (username, mediatype, page, size) => `${environment.ROOT_USER_URL}/${username}/posts/spacemedia/home/${mediatype}/${page}/${size}`;

export const getPublicSpaceMediaUrl = (username, spaceId) => `${environment.ROOT_PUBLIC_URL}/${username}/galleries/media/${spaceId}`;
// same as above with paging support
export const getPublicSpaceMediaPagedUrl = (username, spaceId, mediatype, page, size) => `${environment.ROOT_PUBLIC_URL}/${username}/galleries/media/${spaceId}/${mediatype}/${page}/${size}`;
export const getPublicHomeSpaceMediaUrl = (username) => `${environment.ROOT_PUBLIC_URL}/${username}/galleries/spacemedia/home`;
// same as above but returns paging object
export const getPublicHomeSpaceMediaPagedUrl = (username, mediatype, page, size) => `${environment.ROOT_PUBLIC_URL}/${username}/galleries/spacemedia/home/${mediatype}/${page}/${size}`;

export const getPublicIncrementMediaViewcountUrl = (username, mediaId) => `${environment.ROOT_PUBLIC_URL}/${username}/media/viewcount/increment/${mediaId}`;

// delete a specific media image by id
export const getDeleteSpaceMediaUrl = (username, mediaId) => `${environment.ROOT_USER_URL}/${username}/galleries/media/delete/${mediaId}`;

// state one of 'active' or 'blocked'
export const getPeekUsersUrl = (username, state) => `${environment.ROOT_USER_URL}/${username}/users/peek/${state}`;
export const getPageablePeekUsersUrl = (username, state, page, size) => `${environment.ROOT_USER_URL}/${username}/users/peek/${state}/${page}/${size}`;
export const getSearchPageablePeekUsersUrl = (username, searchterm, page, size) => `${environment.ROOT_USER_URL}/${username}/users/peek/search/${searchterm}/${page}/${size}`;
export const getSearchPageablePeekSpacesUrl = (username, searchterm, page, size) => `${environment.ROOT_USER_URL}/${username}/spaces/peek/search/${searchterm}/${page}/${size}`;

export const getFetchMemberQrCodeUrl = (username, spaceId) => `${environment.ROOT_USER_URL}/${username}/space/${spaceId}/member/qrcode`;
export const getFetchAccountQrCodeUrl = (username) => `${environment.ROOT_USER_URL}/${username}/account/qrcode`;

export const getAddAdminRoleUrl = (username) => `${environment.ROOT_USER_URL}/${username}/role/add/admin`;

export const getRemoveAdminRoleUrl = (username) => `${environment.ROOT_USER_URL}/${username}/role/remove/admin`;

export const getIncrementMediaViewcountUrl = (username, mediaId) => `${environment.ROOT_USER_URL}/${username}/media/viewcount/increment/${mediaId}`;

// members (users) of a specific space
export const getSpacePeekMembersUrl = (username, spaceId, state) =>
    `${environment.ROOT_USER_URL}/${username}/members/${spaceId}/peek/${state}`;

export const getGlobalSearchUrl = (username, searchTerm, size =10) => `${environment.ROOT_USER_URL}/${username}/search/${searchTerm}/${size}`;

export const getUserSearchUrl = (username, searchTerm, size =10) => `${environment.ROOT_USER_URL}/${username}/search/user/${searchTerm}/${size}`;

export const getArchiveSpaceUrl = (username, spaceId) => `${environment.ROOT_USER_URL}/${username}/space/${spaceId}/archive`;

export const getActivateSpaceUrl = (username, spaceId) => `${environment.ROOT_USER_URL}/${username}/space/${spaceId}/activate`;

export const getArchivePageUrl = (username, pageId) => `${environment.ROOT_USER_URL}/${username}/page/${pageId}/archive`;

export const getActivatePageUrl = (username, pageId) => `${environment.ROOT_USER_URL}/${username}/page/${pageId}/activate`;

export const getUpdatePageUrl = (username, pageId) => `${environment.ROOT_USER_URL}/${username}/page/${pageId}/update`;

export const getPublicUserHome = () => `/${environment.DEFAULT_PUBLIC_USER}/home`;

// this is actually the same as above just getting the public user from the server instead of the client env
export const getUserHome = (userHome) => `/${userHome}/home`;

export const getMediaUploadPath = (username, space) => `${environment.ROOT_SERVER_URL}/user/${username}/media/upload/${space}`;

export const getValidateEmailUrl = email => `${environment.ROOT_SERVER_URL}/public/validate/email?value=${email}`;

export const getValidateUsernameUrl = username => `${environment.ROOT_SERVER_URL}/public/validate/username?value=${username}`;

export const getDefaultCopyFile = () => `${environment.DEFAULT_COPY_FILE}.js`;

export const getUpdateSpaceUrl = (username, spaceId) => `${environment.ROOT_USER_URL}/${username}/space/${spaceId}/update`;
export const getUpdatePostVisibilityUrl = (username, postId) => `${environment.ROOT_USER_URL}/${username}/posts/${postId}/visibility`;

export const getUpdateSpaceMediaUrl = (username, spaceId) => `${environment.ROOT_USER_URL}/${username}/galleries/spacemedia/${spaceId}/update`;
export const getLikeSpaceMediaUrl = (username, spaceId, mediaId) => `${environment.ROOT_USER_URL}/${username}/galleries/spacemedia/${spaceId}/${mediaId}/like`;
export const getUpdateMediaUrl = (username, spaceId) => `${environment.ROOT_USER_URL}/${username}/media/${spaceId}/update`;
export const getLikeMediaUrl = (username, spaceId, mediaId) => `${environment.ROOT_USER_URL}/${username}/media/${spaceId}/${mediaId}/like`;

export const getUpdateMembershipUrl = (username, membership) => `${environment.ROOT_USER_URL}/${username}/membership/${membership}`;

export const getCreateSpaceUrl = (username, spaceId) => `${environment.ROOT_USER_URL}/${username}/space/create`;
export const getCreateGalleryBySpaceUrl = (username, spaceId) => `${environment.ROOT_USER_URL}/${username}/space/create/gallery/${spaceId}`;

export const getRenewAuthTokenUrl = () => `${environment.ROOT_SERVER_URL}/public/token`;

export const getUserIsFriendFromUrl = (username, friendName) => `${environment.ROOT_USER_URL}/${username}/friends/from/${friendName}`;
export const getPrincipalMembershipUrl = (username) => `${environment.ROOT_USER_URL}/${username}/membership`;

// console.log('ENVIRONMENT', environment);
